const PRS_CDN_HOST = 'prs.ohousecdn.com';
const IMAGE_CDN_HOST = 'image.ohousecdn.com';
const LOCAL_IMAGE_HOST = 'image.ohou.se';
const LOCAL_SERVICE_BUCKET = 'bucketplace-v2-development';
const ARCHIVED_LEGACY_BUCKETS = ['d224jl0o7z9gbn', 'd12gkpu9h0k5iq'];
const GLOBAL_SERVICE_BUCKETS = [
    'video-service-prd-s3-bucket-thumbnail',
    'oh-image-stage',
    'oh-image-ap-northeast-2',
    'oh-image-ap-southeast-1',
    'oh-image-eu-central-1',
    'oh-image-us-east-1',
    'oh-image-us-west-2',
    'oh-s3-video-qa-thumbnail',
];
// Small helper for type inference.
function createImageUrlMatcher(entry) {
    return entry;
}
const MATCHER_RULES = [
    // If no src is provided, return empty string.
    createImageUrlMatcher({
        test: (ctx) => ctx.options.src === '',
        process: (ctx) => ctx.options.src,
    }),
    /**
     * `*ohousecdn.com` passes through.
     *
     * E.g., https://image.ohousecdn.com/i/bucketplace-v2-development/156110053885150795.jpeg
     * E.g., https://prs.ohousecdn.com/apne2/any/test/disable-cache/v1-313103641010176.jpg?w=720&q=80
     */
    createImageUrlMatcher({
        test: (ctx) => {
            return /^(?:(?:\w+-)?prs|image)\.ohousecdn\.com/.test(ctx.url.hostname);
        },
        process: (ctx) => ctx.url,
    }),
    /**
     * Rewrites PRS resources to `*prs.ohousecdn.com`.
     *
     * The prefix is optional and can be one of the following:
     * - `qa-`
     * - `dev-`
     *
     * E.g., https://prs.ohou.se/kr/any/examples/v1-141430605185088.jpg
     * E.g., https://prs.ohouse.com/apne2/any/uploads/commerce/event/raffle/v1-175699276497024.png
     */
    createImageUrlMatcher({
        locals: {
            envPrefix: undefined,
        },
        test: (ctx) => {
            const matches = /^(?<prefix>\w+-)?prs\.(?:ohou\.se|ohouse\.com)/.exec(ctx.url.hostname);
            if (matches == null) {
                return false;
            }
            ctx.locals.envPrefix = matches.groups?.prefix;
            return true;
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            // Rewrite the hostname to `*prs.ohousecdn.com`
            newUrl.hostname = `${ctx.locals.envPrefix ?? ''}${PRS_CDN_HOST}`;
            return newUrl;
        },
    }),
    /**
     * Rewrites resources from the image server to `image.ohousecdn.com`.
     *
     * By specifying the bucket name in the path, the CDN will be able to serve
     * exotic resources as well.
     *
     * Both `image.ohou.se` and `image.ohouse.com` are supported through the same
     * host `image.ohousecdn.com`, using the fact that the first part of the path
     * is the bucket name.
     *
     * E.g. https://image.ohou.se/i/bucketplace-v2-development/uploads/productions/164999964262228009.jpg?gif=1&w=640&h=640&c=c&webp=1
     * E.g. https://image.ohouse.com/oh-image-ap-northeast-2/uploads/7510f989-a084-414d-9fff-627aada6c685-1.jpeg?w=256&h=256&c=c&w=72&h=72&q=60
     */
    createImageUrlMatcher({
        locals: {
            envPrefix: undefined,
        },
        test: (ctx) => {
            // Handles 'local' image server URLs.
            if (ctx.url.hostname === LOCAL_IMAGE_HOST) {
                return ctx.url.pathname.startsWith('/i/');
            }
            // Handles image server resources hosted on the global service.
            // Note that this will not handle patterns like `qa-image.ohouse.com` which is unsupported by `ohousecdn.com` yet.
            const matches = /^(?<prefix>\w+-)?image\.ohouse\.com/.exec(ctx.url.hostname);
            if (matches != null) {
                ctx.locals.envPrefix = matches.groups?.prefix;
                return true;
            }
            return false;
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            const { envPrefix } = ctx.locals;
            newUrl.hostname = envPrefix != null ? `${envPrefix}${IMAGE_CDN_HOST}` : IMAGE_CDN_HOST;
            return newUrl;
        },
    }),
    /**
     * Rewrites the global video server thumbnail URLs to corresponding global image server URLs.
     *
     * Such as: /oh-s3-video-{slug}/*
     *
     * E.g., /oh-s3-video-apne2-stage-thumbnail/65685e94888fb53cb1f2e33c/65685e94888fb53cb1f2e33c.0000001.jpg
     */
    createImageUrlMatcher({
        test: (ctx) => {
            return ctx.isRelativeUrl && ctx.url.pathname.startsWith('/oh-s3-video');
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            // Although this might not be a reliable way to determine the environment,
            // we are preserving the original code here to avoid breaking the existing
            // behavior.
            const nonProdEnv = ['dev', 'stage', 'qa'].find((env) => ctx.url.pathname.includes(env));
            newUrl.hostname = nonProdEnv != null ? `${nonProdEnv}-${IMAGE_CDN_HOST}` : IMAGE_CDN_HOST;
            return newUrl;
        },
    }),
    /**
     * Rewrites the legacy image server URLs to `image.ohousecdn.com`.
     *
     * The last segment in the paths represents the image size in pixels, which
     * will not be respected during the rewrite. So you might need to reapply the
     * image options if necessary.
     *
     * Such as: /image/(central_crop|resize)/{bucket}/{path..}
     *
     * E.g.,
     * https://image.ohou.se/image/central_crop/bucketplace-v2-development/uploads-users-profile_images-169998132565508046.jpeg/80/80
     */
    createImageUrlMatcher({
        locals: {
            bucket: undefined,
            path: undefined,
        },
        test: (ctx) => {
            if (ctx.url.hostname !== LOCAL_IMAGE_HOST) {
                return false;
            }
            // The `path` is encoded with `-` instead of `/`.
            const matches = /^\/image\/(?:central_crop|resize)\/(?<bucket>[^/]+)\/(?<path>[^/]+)\/.*/.exec(ctx.url.pathname);
            if (matches == null ||
                matches.groups?.bucket == null ||
                matches.groups?.path == null) {
                return false;
            }
            ctx.locals.bucket = matches.groups.bucket;
            ctx.locals.path = matches.groups.path;
            return true;
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            const { bucket, path } = ctx.locals;
            newUrl.hostname = IMAGE_CDN_HOST;
            newUrl.pathname = `/i/${bucket}/${path.replace(/-/g, '/')}`;
            return newUrl;
        },
    }),
    /**
     * Rewrites raw S3 URLs (type A) to `image.ohousecdn.com`.
     *
     * E.g., https://s3-{region}.amazonaws.com/{bucket}/{path}
     */
    createImageUrlMatcher({
        locals: {
            bucket: undefined,
            path: undefined,
        },
        test: (ctx) => {
            // The `region` part in the URL is not important because the image server
            // itself encodes the region corresponding to the bucket name.
            if (!/s3-(?:[^.]+)\.amazonaws.com/.test(ctx.url.hostname)) {
                return false;
            }
            const matches = /^\/(?<bucket>[^/]+)\/(?<path>.+)/.exec(ctx.url.pathname);
            if (matches == null ||
                matches.groups?.bucket == null ||
                matches.groups?.path == null) {
                return false;
            }
            ctx.locals.bucket = matches.groups.bucket;
            ctx.locals.path = matches.groups.path;
            return true;
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            const { bucket, path } = ctx.locals;
            const bucketName = ARCHIVED_LEGACY_BUCKETS.includes(bucket)
                ? LOCAL_SERVICE_BUCKET
                : bucket;
            newUrl.hostname = IMAGE_CDN_HOST;
            newUrl.pathname = `/i/${bucketName}/${path}`;
            return newUrl;
        },
    }),
    /**
     * Rewrites raw S3 URLs (type B) or CloudFront edge URLs to `image.ohousecdn.com`.
     *
     * E.g., https://{bucket}.cloudfront.net/*
     * E.g., https://{bucket}.s3.amazonaws.com/*
     * E.g., https://{bucket}.s3.{region}.amazonaws.com/{path}
     */
    createImageUrlMatcher({
        locals: {
            bucket: undefined,
        },
        test: (ctx) => {
            const matches = /(?<bucket>[^.]+)\.(?:cloudfront.net|s3\.(?:[^.]+\.)?amazonaws\.com)/.exec(ctx.url.hostname);
            if (matches == null || matches.groups?.bucket == null) {
                return false;
            }
            ctx.locals.bucket = matches.groups.bucket;
            return true;
        },
        process: (ctx) => {
            const newUrl = new URL(ctx.url.toString());
            const { bucket } = ctx.locals;
            const bucketName = ARCHIVED_LEGACY_BUCKETS.includes(bucket)
                ? LOCAL_SERVICE_BUCKET
                : bucket;
            if (!GLOBAL_SERVICE_BUCKETS.includes(bucketName)) {
                // If the bucket is not a global service, rewrite it to the local image server.
                newUrl.hostname = IMAGE_CDN_HOST;
                newUrl.pathname = `/i/${bucketName}${ctx.url.pathname}`;
            }
            else if (bucketName.includes('qa')) {
                // If the bucket is a QA bucket, rewrite to the custom QA CDN.
                // Allegedly, this CDN is supposed to be used for video thumbnails.
                newUrl.hostname = `qa-${IMAGE_CDN_HOST}`;
                newUrl.pathname = `/${bucketName}${ctx.url.pathname}`;
            }
            else {
                // Otherwise, rewrite to the normal cdn.
                newUrl.hostname = IMAGE_CDN_HOST;
                newUrl.pathname = `/${bucketName}${ctx.url.pathname}`;
            }
            return newUrl;
        },
    }),
];
/**
 * Normalizes the given image params and returns the transformed image URL.
 *
 * @param imageOptions The image options to transform.
 * @returns The transformed image URL.
 */
export default function getImageUrl(imageOptions) {
    for (const rule of MATCHER_RULES) {
        const locals = rule.locals ?? {};
        // Adding a dummy protocol to avoid parsing errors.
        const url = new URL(imageOptions.src, 'https://_');
        const ctx = {
            url,
            isRelativeUrl: isRelativeUrl(imageOptions.src),
            options: imageOptions,
            locals,
        };
        const shouldProcess = rule.test(ctx);
        if (!shouldProcess) {
            continue;
        }
        const res = rule.process(ctx);
        // If the result is a string, return it as-is.
        if (typeof res === 'string') {
            return res;
        }
        // Otherwise, merge imageOptions in a way not to override the existing
        // query parameters and return the URL.
        if (!rule.skipImageOptionsMerge) {
            const params = buildCdnOptionsParams(imageOptions);
            mergeSearchParamsNonDestructive(res.searchParams, params);
        }
        return res.toString();
    }
    // If no rule is matched, return the original URL as-is.
    return imageOptions.src;
}
function buildCdnOptionsParams({ width, height, mode, quality }) {
    const params = new URLSearchParams();
    if (width != null) {
        params.append('w', String(width));
    }
    if (height != null) {
        params.append('h', String(height));
    }
    if (mode != null) {
        params.append('c', String(mode[0]));
    }
    if (quality != null) {
        params.append('q', String(quality));
    }
    return params;
}
function mergeSearchParamsNonDestructive(baseParams, targetParams) {
    for (const [key, value] of targetParams) {
        if (!baseParams.has(key)) {
            baseParams.append(key, value);
        }
    }
}
function isRelativeUrl(input) {
    return input.startsWith('/');
}
export function getImageUrlSingleAxis(src, width, aspect, quality, useHeight) {
    if (aspect != null) {
        return getImageUrl({
            src,
            width: useHeight ? width * aspect : width,
            height: useHeight ? width : width * aspect,
            mode: 'cover',
            quality,
        });
    }
    return getImageUrl({
        src,
        width: useHeight ? undefined : width,
        height: useHeight ? width : undefined,
        mode: undefined,
        quality,
    });
}
//# sourceMappingURL=getImageUrl.js.map